import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, X, BookOpen } from 'lucide-react';
import { books } from '../data/universeData';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const BooksSection = () => {
  const [selectedBook, setSelectedBook] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyStatus, setNotifyStatus] = useState({});

  const handleNotify = async (bookId) => {
    if (!notifyEmail.trim() || !notifyEmail.includes('@')) return;
    try {
      await fetch(`${API}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: notifyEmail.trim(), source: `book_${bookId}_notify` }),
      });
      setNotifyStatus(prev => ({ ...prev, [bookId]: true }));
      setTimeout(() => setNotifyStatus(prev => ({ ...prev, [bookId]: false })), 3000);
    } catch { }
  };

  return (
    <section 
      id="books" 
      data-testid="books-section"
      className="py-24 px-6 bg-gradient-to-b from-[#080C10] to-[#0a0e12]"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[#C9A84C] mb-4" data-testid="books-title">
            THE KILLING OVER WATER SERIES
          </h2>
          <p className="font-['Outfit'] text-[#E8E0D0]/60 text-base tracking-wide">
            Books dropping throughout 2026–2027
          </p>
        </motion.div>

        {/* Books Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {books.map((book, index) => (
            <motion.div
              key={book.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="book-card bg-[#111820] rounded-lg p-6 flex flex-col"
              data-testid={`book-card-${book.id}`}
            >
              {/* Book Number Badge */}
              <div className="flex items-center justify-between mb-4">
                <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-widest uppercase">
                  Book {book.id}
                </span>
                <span className={`font-['Outfit'] text-xs px-3 py-1 rounded-full ${
                  book.available 
                    ? 'bg-[#C9A84C]/10 text-[#C9A84C]' 
                    : 'bg-[#1A2535] text-[#E8E0D0]/50'
                }`}>
                  {book.status}
                </span>
              </div>

              {/* Book Title */}
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0] mb-2">
                KILLING OVER WATER:
              </h3>
              <h4 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#C9A84C] mb-1">
                {book.title}
              </h4>
              
              {/* Subtitle if exists */}
              {book.subtitle && (
                <p className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-wider mb-2">
                  {book.subtitle}
                </p>
              )}

              {/* Years */}
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm mb-4">
                {book.years}
              </p>

              {/* Chapter count if available */}
              {book.chapters && (
                <div className="flex items-center gap-2 mb-4 text-[#C9A84C]/60">
                  <BookOpen size={14} />
                  <span className="font-['Outfit'] text-xs">
                    {book.chapters.length} Chapters
                  </span>
                </div>
              )}

              {/* Spacer */}
              <div className="flex-grow" />

              {/* Actions */}
              <div className="mt-4">
                {book.status === 'Coming Soon' && (
                  <div className="mb-3">
                    {notifyStatus[book.id] ? (
                      <p className="font-['Outfit'] text-[#4ADE80] text-xs tracking-wider text-center py-2">You'll be the first to know.</p>
                    ) : (
                      <div className="flex gap-1.5">
                        <input type="email" value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="flex-1 bg-[#0a0e12] border border-[#1A2535] rounded px-3 py-1.5 font-['Outfit'] text-[#E8E0D0] text-xs placeholder-[#E8E0D0]/20 focus:border-[#C9A84C]/40 focus:outline-none"
                          data-testid={`book-notify-email-${book.id}`}
                        />
                        <button onClick={() => handleNotify(book.id)}
                          className="bg-[#C9A84C] text-[#080C10] py-1.5 px-3 rounded text-xs font-['Outfit'] font-semibold tracking-wide hover:bg-[#d4b55d] transition-colors shrink-0"
                          data-testid={`book-notify-${book.id}`}>
                          NOTIFY ME
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => setSelectedBook(book)}
                    className="btn-gold flex-1 py-2 px-4 rounded text-sm font-['Outfit'] tracking-wide flex items-center justify-center gap-2"
                    data-testid={`book-read-more-${book.id}`}
                  >
                    READ MORE
                    <ChevronRight size={16} />
                  </button>
                  {book.available && (
                    <button
                      className="bg-[#C9A84C] text-[#080C10] py-2 px-4 rounded text-sm font-['Outfit'] font-semibold tracking-wide hover:bg-[#d4b55d] transition-colors"
                      data-testid={`book-get-${book.id}`}
                    >
                      GET IT
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Book Detail Modal */}
      <AnimatePresence>
        {selectedBook && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelectedBook(null)}
            data-testid="book-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-[#111820] border border-[#C9A84C]/20 rounded-lg p-6 sm:p-8 max-w-2xl w-full relative my-8 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="book-modal"
            >
              <button
                onClick={() => setSelectedBook(null)}
                className="absolute top-4 right-4 text-[#E8E0D0]/50 hover:text-[#C9A84C] transition-colors"
                data-testid="book-modal-close"
              >
                <X size={24} />
              </button>
              
              <div className="mb-4">
                <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs tracking-widest uppercase">
                  Book {selectedBook.id} — {selectedBook.years}
                </span>
              </div>
              
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0] mb-1">
                KILLING OVER WATER:
              </h3>
              <h4 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[#C9A84C] mb-2">
                {selectedBook.title}
              </h4>
              
              {selectedBook.subtitle && (
                <p className="font-['Outfit'] text-[#C9A84C]/60 text-sm tracking-wider mb-4">
                  {selectedBook.subtitle}
                </p>
              )}
              
              <p className="font-['Outfit'] text-[#E8E0D0]/80 leading-relaxed italic mb-6">
                "{selectedBook.teaser}"
              </p>

              {/* Chapters if available */}
              {selectedBook.chapters && (
                <div className="border-t border-[#C9A84C]/10 pt-6">
                  <h5 className="font-['Outfit'] text-[#C9A84C] text-sm tracking-widest uppercase mb-4">
                    Chapters
                  </h5>
                  <div className="space-y-3">
                    {selectedBook.chapters.map((chapter) => (
                      <div 
                        key={chapter.number}
                        className="bg-[#0a0e12] border border-[#C9A84C]/10 rounded p-4"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-['Outfit'] text-[#C9A84C]/60 text-xs">
                            CH. {chapter.number}
                          </span>
                          <span className="font-['Cormorant_Garamond'] text-[#E8E0D0] font-bold">
                            {chapter.title}
                          </span>
                        </div>
                        <p className="font-['Outfit'] text-[#E8E0D0]/50 text-sm">
                          {chapter.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedBook.available ? (
                <button
                  className="mt-6 w-full bg-[#C9A84C] text-[#080C10] py-3 px-6 rounded font-['Outfit'] font-semibold tracking-wide hover:bg-[#d4b55d] transition-colors"
                  data-testid="book-modal-get"
                >
                  GET THE BOOK
                </button>
              ) : selectedBook.status === 'Coming Soon' && (
                <div className="mt-6">
                  {notifyStatus[selectedBook.id] ? (
                    <p className="font-['Outfit'] text-[#4ADE80] text-sm tracking-wider text-center py-3">You'll be the first to know when it drops.</p>
                  ) : (
                    <div className="flex gap-2">
                      <input type="email" value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="flex-1 bg-[#0a0e12] border border-[#1A2535] rounded px-4 py-3 font-['Outfit'] text-[#E8E0D0] text-sm placeholder-[#E8E0D0]/20 focus:border-[#C9A84C]/40 focus:outline-none"
                        data-testid="book-modal-notify-email"
                      />
                      <button onClick={() => handleNotify(selectedBook.id)}
                        className="bg-[#C9A84C] text-[#080C10] py-3 px-6 rounded font-['Outfit'] font-semibold tracking-wide hover:bg-[#d4b55d] transition-colors shrink-0"
                        data-testid="book-modal-notify">
                        NOTIFY ME
                      </button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default BooksSection;
